<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="t('transformers.conditionStatus.content.overrideModal.title')"
            click-shield
            class="override-dialog"
        >
            <div class="override-modal">
                <div class="override-modal__detail-line">
                    <FormInputField
                        :model-value="contextPayload?.reportName"
                        :label="t('transformers.conditionStatus.content.overrideModal.labels.reportNumber')"
                        disabled
                    />
                    <FormInputField
                        :model-value="reportTypeString"
                        :label="t('transformers.conditionStatus.content.overrideModal.labels.analysisType')"
                        disabled
                    />
                </div>
                <div class="override-modal__detail-line">
                    <VeeFormSingleSelectField
                        name="status"
                        :label="t('transformers.conditionStatus.content.overrideModal.labels.status')"
                        :options="statusOptions"
                    />
                </div>
                <div class="override-modal__textarea-line">
                    <VeeTextareaField
                        name="comment"
                        :label="t('transformers.conditionStatus.content.overrideModal.labels.comment')"
                    />
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('transformers.conditionStatus.content.overrideModal.actions.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.dirty || isMutating"
                :loading="isMutating"
                @click="onSubmit"
            >
                {{ t('transformers.conditionStatus.content.overrideModal.actions.confirm') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { PebbleDialog } from '@pebble/core'
import {
    OverrideReportStatusOpenPayload,
    provideOverrideReportStatusModal,
} from '@/components/modals/override-report-status/useOverrideReportStatusModal'
import { useI18n } from 'vue-i18n'
import FormInputField from '@/components/form-inputs/form-input-field/FormInputField.vue'
import { useForm } from 'vee-validate'
import { reportStatusOverrideValidationSchema } from '@/components/modals/override-report-status/OverrideReportStatusModal.form'
import { ConditionStatus, StatusTypes } from '@/configuration/status/condition-status'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { AxiosError } from 'axios'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import VeeFormSingleSelectField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormSingleSelectField.vue'
import VeeTextareaField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeTextareaField.vue'
import { ComponentType } from '@/configuration/component/component-types'
import { useOverrideReportStatus } from '@/api/mutations/general/useOverrideReportStatus'
import { ReportTypes } from '@/configuration/report/report-types'

const { t } = useI18n()
const toasts = usePebbleToasts()

const props = defineProps<{
    componentId: string,
    componentType: ComponentType,
}>()

const dialogRef = ref<PebbleDialog | null>(null)
const contextPayload = ref<OverrideReportStatusOpenPayload | undefined>()

const { mutateAsync, isPending: isMutating } = useOverrideReportStatus()

const { handleSubmit, resetForm, meta } = useForm({
    validationSchema: reportStatusOverrideValidationSchema,
    initialValues: {
        comment: '',
        status: contextPayload.value?.status,
    },
})

const statusOptions = computed<SelectOptions<number>>(() =>
    StatusTypes.filter((e) => e.value !== ConditionStatus.None).map((e) => ({
        label: t(e.labelKey),
        value: e.value,
    })),
)

const reportTypeString = computed(() => {
    return t(ReportTypes.find((e) => e.value === contextPayload.value?.reportType)?.labelKey ?? '-')
})

const openOverride = (payload: OverrideReportStatusOpenPayload) => {
    contextPayload.value = payload

    resetForm({
        values: {
            comment: '',
            status: payload.status,
        },
    })

    dialogRef.value?.show()
}

const closeModal = () => {
    dialogRef.value?.hide()
}

const onSubmit = handleSubmit(async (values) => {
    if (!contextPayload.value) {
        return
    }

    try {
        await mutateAsync({
            overrideData: {
                reportType: contextPayload.value.reportType,
                reportId: contextPayload.value.reportId,
                newStatus: values.status,
                comment: values.comment,
                componentSid: props.componentId,
                componentType: props.componentType,
            },
        })

        closeModal()
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

provideOverrideReportStatusModal({
    openOverride,
})
</script>

<style scoped lang="scss">
.override-modal {
    display: flex;
    flex-direction: column;

    gap: $spacing-m;

    &__detail-line {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        column-gap: $spacing-m;
    }
}

.override-dialog {
    --width: 70vw;
}
</style>
